import { FC, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  createFilterOptions,
  TextField,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useGetProjectLabelsQuery } from 'redux/apiSlices/labels.slice';
import { ITaskUI } from 'data/types/task.type';
import {
  useAddAndUpdateTaskLabelsMutation,
  useUpdateTaskLabelsMutation,
} from '../../../../../redux/apiSlices/tasks.slice';

interface IOptionType {
  title: string;
  optionLabel: string;
  id?: string;
}

const filter = createFilterOptions<IOptionType>();

// Consistent label colors
const labelColors = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

const getLabelColor = (id?: string) => {
  if (!id) return labelColors[0];
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return labelColors[hash % labelColors.length];
};

const TaskLabels: FC<{
  task: ITaskUI;
}> = ({ task }) => {
  const [updateTaskLabels] = useUpdateTaskLabelsMutation();
  const [addAndUpdateTaskLabels] = useAddAndUpdateTaskLabelsMutation();

  const { data: labels } = useGetProjectLabelsQuery(task.projectId);
  const [value, setValue] = useState<string[]>(task.labels);

  const [selectOptions, setSelectOptions] = useState<IOptionType[]>([]);
  useEffect(() => {
    if (labels?.length) {
      setSelectOptions(
        labels.map(item => ({ ...item, optionLabel: item.title })),
      );
    } else setSelectOptions([]);
  }, [labels]);

  const onUpdateLabels = (labelIds: string[]) => {
    updateTaskLabels({ labels: labelIds, taskId: task.taskId }).unwrap();
    setValue(labelIds);
  };

  const onAddAndUpdate = async (newLabel: string, labelIds: string[]) => {
    const response = await addAndUpdateTaskLabels({
      taskId: task.taskId,
      projectId: task.projectId,
      labelIds,
      newLabel,
    }).unwrap();
    setSelectOptions(prev => [
      ...prev,
      { optionLabel: response.title, title: response.title, id: response.id },
    ]);
    setValue(prev => [...prev, response.id]);
  };

  return (
    <>
      {labels && (
        <Autocomplete
          fullWidth
          multiple
          popupIcon={null}
          value={
            value.map(id => selectOptions.find(item => item.id === id)) || []
          }
          clearOnBlur
          noOptionsText="Type to create a new label"
          options={selectOptions}
          getOptionLabel={option => option?.optionLabel || ''}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option?.id || index}
                label={option?.title}
                size="small"
                sx={{
                  backgroundColor: getLabelColor(option?.id),
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: 24,
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': {
                      color: 'white',
                    },
                  },
                }}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              placeholder={value.length === 0 ? 'Add labels' : ''}
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <LocalOfferOutlinedIcon
                      sx={{ fontSize: 18, ml: 1, mr: 0.5, color: 'text.secondary' }}
                    />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={async (event, newLabels) => {
            const addedLabel = newLabels.find(item => !item?.id);
            if (!addedLabel) {
              const labelIds = newLabels.map(item => item?.id);
              onUpdateLabels(labelIds as string[]);
            } else {
              const temp: string[] = [];
              newLabels.forEach(item => {
                if (item?.id) temp.push(item.id);
              });
              await onAddAndUpdate(addedLabel.optionLabel, temp);
            }
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option?.id || option?.optionLabel}
              sx={{
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {option?.id ? (
                <Chip
                  label={option.title}
                  size="small"
                  sx={{
                    backgroundColor: getLabelColor(option.id),
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                  {option?.title}
                </Box>
              )}
            </Box>
          )}
          filterOptions={(options, params) => {
            const filtered = filter(options as IOptionType[], params);

            const { inputValue } = params;
            const isExisting = options.some(
              option => inputValue === option?.title,
            );
            if (inputValue !== '' && !isExisting) {
              filtered.push({
                title: `Add "${inputValue}"`,
                optionLabel: inputValue,
              });
            }
            return filtered;
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              py: 0.5,
              '& input': {
                py: 0.5,
                fontSize: '0.875rem',
              },
              '& input::placeholder': {
                color: 'text.secondary',
                opacity: 1,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          }}
        />
      )}
    </>
  );
};

export default TaskLabels;
